import { useState, useEffect } from 'react';
import { Button, Modal, toast } from '../../../ui';
import { formatCurrency, formatDateBR } from '../../../utils/format';
import { useCategories } from '../../../hooks/useFinancialOps';
import { useMergeTransactions } from '../hooks/useReconciliation';
import type { MergeBody } from '../services/reconciliation.service';

export interface ReconciliarCandidate {
  id: string;
  description: string;
  amount: number;
  type: 'INCOME' | 'EXPENSE';
  transactionDate: string;
  categoryId: string | null;
}

interface Props {
  open: boolean;
  onClose: () => void;
  candidates: ReconciliarCandidate[];
  defaultSurvivorId?: string;
  onMerged?: (result: { mergedCount: number; affectedPayouts: number }) => void;
}

export function ReconciliarDialog({
  open,
  onClose,
  candidates,
  defaultSurvivorId,
  onMerged,
}: Props) {
  const merge = useMergeTransactions();
  const { data: categoriesData } = useCategories();
  const categories = categoriesData?.data ?? [];

  const [survivorId, setSurvivorId] = useState(defaultSurvivorId ?? candidates[0]?.id ?? '');
  const survivor = candidates.find((c) => c.id === survivorId);

  const [description, setDescription] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionDate, setTransactionDate] = useState('');
  const [categoryId, setCategoryId] = useState('');

  useEffect(() => {
    if (survivor) {
      setDescription(survivor.description);
      setAmount(String(survivor.amount));
      setTransactionDate(survivor.transactionDate.slice(0, 10));
      setCategoryId(survivor.categoryId ?? '');
    }
  }, [survivor]);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!survivor) {
      return;
    }
    const mergedIds = candidates.filter((c) => c.id !== survivor.id).map((c) => c.id);
    if (mergedIds.length === 0) {
      toast.error('Selecione pelo menos 2 transações.');
      return;
    }
    const survivorPatch: MergeBody['survivorPatch'] = {};
    if (description !== survivor.description) {
      survivorPatch.description = description;
    }
    if (Number(amount) !== survivor.amount) {
      survivorPatch.amount = Number(amount);
    }
    if (transactionDate !== survivor.transactionDate.slice(0, 10)) {
      survivorPatch.transactionDate = transactionDate;
    }
    if ((categoryId || null) !== survivor.categoryId) {
      survivorPatch.categoryId = categoryId || null;
    }
    try {
      const out = await merge.mutateAsync({
        survivorId: survivor.id,
        mergedIds,
        survivorPatch: Object.keys(survivorPatch).length ? survivorPatch : undefined,
      });
      const data = out?.data ?? out;
      toast.success(
        `${data.mergedCount} transações mescladas${data.affectedPayouts ? ` (${data.affectedPayouts} payout(s) afetado(s))` : ''}`
      );
      onMerged?.(data);
      onClose();
    } catch (err: any) {
      toast.error(err?.response?.data?.message || err?.message || 'Falha ao mesclar.');
    }
  }

  if (!candidates.length) {
    return null;
  }

  return (
    <Modal open={open} onClose={onClose} title="Conciliar transações">
      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-ink-secondary">Selecione qual transação fica:</p>
          {candidates.map((c) => (
            <label key={c.id} className="flex items-start gap-2 cursor-pointer">
              <input
                type="radio"
                name="survivor"
                checked={survivorId === c.id}
                onChange={() => setSurvivorId(c.id)}
                className="mt-1"
              />
              <span className="text-sm">
                <strong>{c.description}</strong> · {c.type === 'INCOME' ? '+' : '-'}
                {formatCurrency(c.amount)} · {formatDateBR(c.transactionDate)}
              </span>
            </label>
          ))}
        </div>

        {survivor && (
          <div className="space-y-2 border-t pt-4">
            <p className="text-sm text-ink-secondary">Editar campos da sobrevivente (opcional):</p>
            <div className="grid grid-cols-2 gap-2">
              <label className="text-sm">
                Descrição
                <input
                  className="w-full border rounded px-2 py-1"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                />
              </label>
              <label className="text-sm">
                Valor
                <input
                  type="number"
                  step="0.01"
                  className="w-full border rounded px-2 py-1"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                />
              </label>
              <label className="text-sm">
                Data
                <input
                  type="date"
                  className="w-full border rounded px-2 py-1"
                  value={transactionDate}
                  onChange={(e) => setTransactionDate(e.target.value)}
                />
              </label>
              <label className="text-sm">
                Categoria
                <select
                  className="w-full border rounded px-2 py-1"
                  value={categoryId}
                  onChange={(e) => setCategoryId(e.target.value)}
                >
                  <option value="">—</option>
                  {categories
                    .filter((c: any) => c.type === survivor.type)
                    .map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.name}
                      </option>
                    ))}
                </select>
              </label>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <Button type="button" variant="ghost" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" disabled={merge.isPending || candidates.length < 2}>
            {merge.isPending ? 'Mesclando…' : `Mesclar ${candidates.length} → 1`}
          </Button>
        </div>
      </form>
    </Modal>
  );
}
